// Linke Spalte des Chats (iMessage-Stil): laufende Gespräche (1:1 + Gruppen) + „Neu"-Ansicht
// zum Anschreiben eines Kollegen. Bekommt alle Daten per Props (kein eigener useChat()), damit
// der Baustein ohne Kontext testbar bleibt und sein Zweck an der Signatur sichtbar ist.
import { useMemo } from 'react';
import { CaretLeft, ChatCircle, NotePencil, UsersThree } from '@phosphor-icons/react';
import {
  gruppenName, kollegeName, vorschauText, zeitKurz,
  type ChatGruppe, type Kollege, type Unterhaltung,
} from '../../../../lib/chat/nachrichten';
import { vorschauMitAbsender } from '../../../../lib/island/chatDarstellung';
import { FARBE } from '../../pille-stil';
import { Avatar } from './Avatar';
import { NeuDialog } from './NeuDialog';

type Ziel = { art: 'person' | 'gruppe'; id: string };

export function GespraechsListe({
  unterhaltungen, kollegen, gruppen, meineId, aktiv, onWaehlen, onNeu, neuOffen, onGruppe, onFehler,
}: {
  unterhaltungen: Unterhaltung[];
  kollegen: Kollege[];
  gruppen: ChatGruppe[];
  meineId: string | null;
  aktiv: Ziel | null;
  onWaehlen: (ziel: Ziel) => void;
  onNeu: () => void;
  neuOffen: boolean;
  onGruppe: (g: ChatGruppe) => void;
  onFehler?: (msg: string) => void;
}) {
  const namen = useMemo(() => {
    const m = new Map<string, Kollege>();
    for (const k of kollegen) m.set(k.id, k);
    return m;
  }, [kollegen]);

  return (
    <div className={`${aktiv ? 'hidden sm:flex' : 'flex'} w-full flex-col sm:w-[250px] sm:shrink-0`} style={{ gap: 8 }}>
      <div className="flex items-center justify-between" style={{ padding: '0 4px' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: FARBE.text2 }}>{neuOffen ? 'Neues Gespräch' : 'Chats'}</span>
        <button
          type="button"
          onClick={onNeu}
          aria-label={neuOffen ? 'Zurück' : 'Neues Gespräch'}
          title={neuOffen ? 'Zurück' : 'Neues Gespräch'}
          className="pille-druck flex cursor-pointer items-center justify-center"
          style={{ width: 36, height: 36, borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.08)', color: '#64d2ff' }}
        >
          {neuOffen ? <CaretLeft size={17} weight="bold" /> : <NotePencil size={18} weight="fill" />}
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" style={{ gap: 2 }}>
        {neuOffen ? (
          <NeuDialog
            kollegen={kollegen}
            meineId={meineId || ''}
            onPerson={(id) => onWaehlen({ art: 'person', id })}
            onGruppe={onGruppe}
            onFehler={onFehler}
          />
        ) : unterhaltungen.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center" style={{ gap: 8, padding: '0 12px', color: 'rgba(235,235,245,0.45)' }}>
            <ChatCircle size={26} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Noch keine Nachrichten</span>
            <button type="button" onClick={onNeu} className="cursor-pointer hover:underline"
              style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: '#64d2ff' }}>
              Gespräch beginnen
            </button>
          </div>
        ) : (
          unterhaltungen.map((u) => {
            const istGruppe = u.art === 'gruppe';
            const gruppe = istGruppe ? gruppen.find((g) => g.id === u.id) : null;
            const name = istGruppe ? (gruppe ? gruppenName(gruppe) : 'Gruppe') : kollegeName(namen.get(u.id));
            const istAktiv = aktiv?.art === u.art && aktiv.id === u.id;
            const vorschau = u.letzte
              ? vorschauMitAbsender(vorschauText(u.letzte), u.letzte.absender_id === meineId, istGruppe, kollegeName(namen.get(u.letzte.absender_id)))
              : 'Noch keine Nachrichten';
            return (
              <button
                key={`${u.art}:${u.id}`}
                type="button"
                onClick={() => onWaehlen({ art: u.art, id: u.id })}
                aria-current={istAktiv || undefined}
                className="pille-chatzeile flex w-full cursor-pointer items-center text-left"
                style={{ gap: 10, padding: '8px 8px', borderRadius: 14, border: 'none', fontFamily: 'inherit', color: '#fff', background: istAktiv ? 'rgba(10,132,255,0.22)' : undefined }}
              >
                <span className="relative shrink-0">
                  <Avatar name={name} bild={istGruppe ? null : namen.get(u.id)?.avatar_url} groesse={46} />
                  {/* Team-Chats erben den Teamnamen — das Zeichen macht sie zusätzlich am Icon erkennbar. */}
                  {istGruppe && gruppe?.team_id && (
                    <span className="absolute flex items-center justify-center rounded-full" style={{ right: -2, bottom: -2, width: 18, height: 18, background: '#1c1c1e', color: FARBE.gold }} aria-hidden>
                      <UsersThree size={11} weight="fill" />
                    </span>
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
                  <span className="flex items-center justify-between" style={{ gap: 6 }}>
                    <span className="truncate" style={{ fontSize: 15, fontWeight: u.ungelesen ? 800 : 600 }}>{name}</span>
                    <span className="shrink-0" style={{ fontSize: 11, color: 'rgba(235,235,245,0.45)' }}>{u.letzte ? zeitKurz(u.letzte.created_at) : ''}</span>
                  </span>
                  <span className="flex items-start" style={{ gap: 6 }}>
                    <span className="pille-zweizeilig min-w-0 flex-1" style={{ fontSize: 12.5, lineHeight: 1.3, color: u.ungelesen ? 'rgba(255,255,255,0.85)' : 'rgba(235,235,245,0.55)' }}>
                      {vorschau}
                    </span>
                    {u.ungelesen > 0 && (
                      <span className="shrink-0 tabular-nums" aria-label={`${u.ungelesen} ungelesen`}
                        style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, fontSize: 11, fontWeight: 800, lineHeight: '20px', textAlign: 'center', background: FARBE.blau }}>
                        {u.ungelesen}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
